import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { DirectReportAPIData, DirectReportEntity, UserDetailAPIData, UserDetailEntity } from '@bonusly/user-entities';

/**
 * @deprecated
 */
export const useUser = (id?: string) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!id,
		queryKey: ['user', id],
		queryFn: async () => {
			const res = await http.get<UserDetailAPIData>(`/user_management/users/${id}`);
			const data = res.data;

			return new UserDetailEntity({
				createdAt: new Date(data.created_at),
				customProperties: data.custom_properties,
				displayFirstName: data.display_first_name,
				displayName: data.display_name,
				email: data.email,
				firstName: data.first_name,
				id: data.id,
				lastName: data.last_name,
				locale: data.locale,
				managerId: data.manager_id,
				profilePic: data.profile_pic,
				timeZone: data.time_zone,
				updatedAt: new Date(data.updated_at),
				userMode: data.user_mode,
				username: data.username,
				status: data.status,
				deletedAt: data.deleted_at,
			});
		},
	});
};

/**
 * @deprecated
 */
export const useUserIncludeDeactivated = (id?: string) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!id,
		queryKey: ['user-include-deactivated', id],
		queryFn: async () => {
			const res = await http.get<UserDetailAPIData>(`/user_management/users/${id}/active_or_deactivated`);

			const data = res.data;

			return new UserDetailEntity({
				createdAt: new Date(data.created_at),
				displayFirstName: data.display_first_name,
				displayName: data.display_name,
				email: data.email,
				firstName: data.first_name,
				id: data.id,
				lastName: data.last_name,
				locale: data.locale,
				managerId: data.manager_id,
				profilePic: data.profile_pic,
				timeZone: data.time_zone,
				updatedAt: new Date(data.updated_at),
				userMode: data.user_mode,
				username: data.username,
				status: data.status,
				deletedAt: data.deleted_at,
				customProperties: data.custom_properties,
			});
		},
	});
};

/**
 * @deprecated
 */
export const useUsers = (ids?: string[]) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!ids,
		queryKey: ['user', ids],
		queryFn: async () => {
			if (!ids) {
				return [];
			}

			const sliceSize = 1000;

			const slices = ids.reduce((resultArray, item, index) => {
				const sliceIndex = Math.floor(index / sliceSize);
				if (!resultArray[sliceIndex]) {
					resultArray[sliceIndex] = [];
				}

				resultArray[sliceIndex].push(item);
				return resultArray;
			}, [] as string[][]);

			const requests = slices.map((slice) => {
				return http.post<UserDetailAPIData[]>('/user_management/users/bulk', {
					user_ids: slice,
				});
			});

			const promisesResult = await Promise.all(requests);

			const result = promisesResult.flatMap((res) => {
				return res.data;
			});

			return result.map((data) => {
				return new UserDetailEntity({
					createdAt: new Date(data.created_at),
					customProperties: data.custom_properties,
					displayFirstName: data.display_first_name,
					displayName: data.display_name,
					email: data.email,
					firstName: data.first_name,
					id: data.id,
					lastName: data.last_name,
					locale: data.locale,
					managerId: data.manager_id,
					profilePic: data.profile_pic,
					updatedAt: new Date(data.updated_at),
					userMode: data.user_mode,
					username: data.username,
					status: data.status,
					timeZone: data.time_zone,
					deletedAt: data.deleted_at,
				});
			});
		},
	});
};

type DirectReportsApiResponse = {
	data: {
		users: DirectReportAPIData[];
	};
};

/**
 * @deprecated
 */
export const useDirectReports = (userId: string) => {
	const http = useHttpClient('v1');

	return useQuery({
		queryKey: ['direct-reports', userId],
		queryFn: async () => {
			const res = await http.get<DirectReportsApiResponse>(`/users/${userId}/direct_reports.json`);

			return res.data.data.users.map(
				(user) =>
					new DirectReportEntity({
						displayName: user.display_name,
						email: user.email,
						id: user.id,
						lastRecognitionAt: new Date(user.last_recognition_at),
						profilePic: user.profile_pic_url,
						recognitionCount: user.recognition_count,
						username: user.username,
						deletedAt: user.deleted_at,
					})
			);
		},
	});
};